import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { strengths, contactInfo } from '../data';

export default function Home() {
  const [references, setReferences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [refsRes, testRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/referencer'),
          fetch('https://api.dev.auxo.dk/public/content/kundeudtalelser')
        ]);
        
        if (refsRes.ok) {
          const refsData = await refsRes.json();
          setReferences(refsData.slice(0, 3));
        } else {
          setReferences(fallbackReferences);
        }
        if (testRes.ok) {
          const testData = await testRes.json();
          setTestimonials(testData);
        } else {
          setTestimonials(fallbackTestimonials);
        }
      } catch (err) {
        console.error('Error fetching CMS data:', err);
        setReferences(fallbackReferences);
        setTestimonials(fallbackTestimonials);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="overflow-hidden bg-white">
      <SEO />

      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-96px)] flex items-center bg-stone-950 text-white">
        {/* Background Image with Premium Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg" 
            alt="Kvalitetsbyggeri i træ af Munkgaard Træbyg ApS" 
            className="w-full h-full object-cover opacity-40 select-none pointer-events-none"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-9 flex flex-col items-start gap-6">
            <span className="text-accent-light font-semibold tracking-[0.25em] uppercase text-xs sm:text-sm bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/10">
              VELKOMMEN TIL MUNKGAARD TRÆBYG APS
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-white max-w-4xl">
              Tømrer – vi bygger med <span className="text-accent-light">træ</span> og <span className="italic font-normal text-stone-200">tillid</span>
            </h1>
            <p className="text-stone-300 text-base sm:text-lg md:text-xl max-w-3xl leading-relaxed">
              Velkommen til Munkgaard Træbyg. Søger du en engageret tømrer i Bording, Ikast, Engesvang, Silkeborg eller omegn? Når du overvejer en ny tilbygning, renovering, udhus eller kvalitetsbyggeri i træ, er du kommet til det rette sted. Vi har et bredt erfaringsgrundlag inden for garager, tilbygninger, renoveringer, helårshuse og gennemtænkte trækonstruktioner – altid med fokus på det gode håndværk og rådgivning gennem hele forløbet.
            </p>

            {/* Hero Badges */}
            <div className="flex flex-wrap gap-4 mt-2">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-lg text-sm font-medium text-stone-200">
                <i className="fa-solid fa-circle-check text-accent-light" />
                Én fast kontaktperson
              </div>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-lg text-sm font-medium text-stone-200">
                <i className="fa-solid fa-circle-check text-accent-light" />
                Gennemarbejdet kvalitet
              </div>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-lg text-sm font-medium text-stone-200">
                <i className="fa-solid fa-circle-check text-accent-light" />
                Præcision & ordentlighed
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-6 w-full sm:w-auto">
              <Link 
                to="/vi-tilbyder" 
                className="bg-accent hover:bg-accent-light text-white px-8 py-4 rounded-md text-base font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-center"
              >
                Se hvad vi tilbyder
              </Link>
              <Link 
                to="/kontakt" 
                className="border border-white/30 hover:border-white bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-md text-base font-semibold tracking-wide transition-all duration-300 text-center"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Træbyggeri & Tømrerarbejde i Fokus */}
      <section className="py-24 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Image Side with Premium Stacked Layout */}
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-stone-200/40">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214047.jpg" 
                alt="Smukt håndværk i træ af Munkgaard Træbyg" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-103"
              />
            </div>
            <div className="absolute -bottom-8 -right-6 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl border-4 border-white hidden sm:block">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260309-152705.jpg" 
                alt="Gennemført træbyggeri detalje" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content Side */}
          <div className="lg:col-span-6 flex flex-col items-start gap-6">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              MUNKGAARD TRÆBYG APS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Bred erfaring med træbyggeri og personlig kontakt hele vejen
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Munkgaard Træbyg ApS er en enkeltmandsvirksomhed opbygget omkring stærke håndværksmæssige værdier: tæt personlig kontakt, rådgivning gennem hele projektet, ordentlighed og højt fagligt niveau. Vi løser opgaver for kunder i hele området omkring Bording, Ikast, Engesvang, Kragelund og Silkeborg samt omkringliggende landområder.
            </p>
            <p className="text-stone-600 text-base leading-relaxed">
              Vores erfaring spænder bredt – fra garager, annekser, skure og udhuse til tilbygninger, totalrenoveringer, kommende helårshuse i træ samt større værkstedsbyggerier og andre konstruktioner, hvor træ anvendes som det bærende og æstetiske materiale.
            </p>

            <div className="bg-stone-50 border-l-4 border-accent p-6 rounded-r-xl my-2">
              <h4 className="font-display font-bold text-stone-900 text-lg mb-2">
                Samme tømrer på opgaven fra første dialog til færdigt resultat
              </h4>
              <p className="text-stone-600 text-sm leading-relaxed">
                Som enkeltmandsvirksomhed er det mig selv, der rådgiver dig, måler op og udfører selve tømrerarbejdet. Det giver dig en tryg proces, gennemskuelige aftaler og garanti for, at intet går tabt i kommunikationen.
              </p>
            </div>

            <Link 
              to="/om-os" 
              className="group flex items-center gap-2 text-accent font-semibold hover:text-accent-light transition-colors mt-2 text-sm"
            >
              <span>Læs mere om Munkgaard Træbyg</span>
              <i className="fa-solid fa-arrow-right transition-transform duration-300 group-hover:translate-x-1 text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* Hvad kan vi tilbyde? */}
      <section className="py-24 bg-stone-50 border-t border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              Vores ydelser
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
              Hvad kan vi tilbyde?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base">
              Hos Munkgaard Træbyg mestrer vi alle facetter af tømrerfaget. Vi har særligt fokus på træbyggeri, der forener æstetik og holdbarhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceTeaserCard 
              title="Udhuse" 
              href="/vi-tilbyder/udhuse" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250716154846.jpg"
              desc="Garager, carporte, annekser og skure i træ – opført med gennemtænkte detaljer og holdbare konstruktioner."
            />
            <ServiceTeaserCard 
              title="Tilbygning" 
              href="/vi-tilbyder/tilbygning" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg"
              desc="Få mere plads med en arkitektonisk harmonisk tilbygning i træ, der passer perfekt til dit eksisterende hus."
            />
            <ServiceTeaserCard 
              title="Nybyggeri i træ" 
              href="/vi-tilbyder/nybyggeri-i-trae" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg"
              desc="Nye sommerhuse og moderne helårshuse opført med træ som bærende og æstetisk element."
            />
            <ServiceTeaserCard 
              title="Totalrenovering" 
              href="/vi-tilbyder/totalrenovering" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154328.jpg"
              desc="Gennemgribende modernisering af din bolig med respekt for husets oprindelige karakter og sjæl."
            />
            <ServiceTeaserCard 
              title="Service" 
              href="/vi-tilbyder/service" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg"
              desc="Alt inden for døre, vinduer, tag, træbeklædning, gulve, køkken og udendørs terrasser."
            />
            <ServiceTeaserCard 
              title="Vores styrker" 
              href="/vores-styrker" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=500,height=330,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260310-142612_6192842c.jpg"
              desc="Læs om vores fokus på god kommunikation, ordentlighed, DM i Skills-faglighed og overholdte aftaler."
            />
          </div>
        </div>
      </section>

      {/* Gennemtænkte løsninger og gennemførte detaljer */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Content Side */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              KVALITETSHÅNDVÆRK
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Gennemtænkte løsninger og gennemførte detaljer
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Hos Munkgaard Træbyg ApS lægger vi vægt på, at tingene hænger sammen – både teknisk og visuelt. Et godt resultat handler ikke kun om selve udførelsen, men om at vælge de rigtige løsninger fra starten og få detaljerne til at spille. Vi arbejder med løsninger, hvor funktion, materialevalg og finish går op i en højere enhed.
            </p>

            <div className="w-full">
              <h4 className="font-display font-bold text-stone-900 text-base mb-4">
                Du kan blandt andet få hjælp til:
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <li className="flex items-start gap-3 text-sm text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center text-accent text-xs shrink-0 mt-0.5">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <span>Garager, carporte, annekser og redskabsskure i træ</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center text-accent text-xs shrink-0 mt-0.5">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <span>Gennemtænkte tilbygninger med naturlig sammenhæng</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center text-accent text-xs shrink-0 mt-0.5">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <span>Komplette træbyggerier – herunder sommerhuse og helårshuse</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700">
                  <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center text-accent text-xs shrink-0 mt-0.5">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <span>Gennemgribende renovering og tømrerservice</span>
                </li>
              </ul>
            </div>

            <p className="text-stone-600 text-base leading-relaxed italic border-l-2 border-stone-300 pl-4 mt-2">
              Uanset opgavens størrelse går vi grundigt til værks og sikrer, at arbejdet er gennemtænkt fra start. Du får en løsning, der fungerer i praksis og fremstår pæn og veludført – både nu og på sigt.
            </p>
          </div>

          {/* Image Side */}
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-stone-200/40">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg" 
                alt="Gennemtænkt tømrerarbejde" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tømrermester, der gør det overskueligt at komme i mål */}
      <section className="py-24 bg-stone-50 border-t border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Image Side */}
          <div className="lg:col-span-5 order-last lg:order-first">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-stone-200/40 group">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260310-175448.jpg" 
                alt="Jacob Munkgaard Sørensen – Tømrermester" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-stone-950/80 via-stone-950/30 to-transparent p-6 text-white">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Munkgaard Træbyg ApS</span>
                <p className="text-lg font-bold text-white mt-0.5">Jacob Munkgaard Sørensen</p>
                <p className="text-xs text-stone-300">Tømrermester & Indehaver</p>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              TRYG PROCES
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Tømrermester, der gør det overskueligt at komme i mål
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Mange husejere har prøvet at stå med et byggeprojekt, der føles uoverskueligt. Hos Munkgaard Træbyg lægger vi vægt på klar dialog, realistiske aftaler og et tydeligt overblik, så du ikke skal bekymre dig undervejs.
            </p>
            <p className="text-stone-600 text-base leading-relaxed">
              Uanset om du planlægger en ny tilbygning for at få ekstra plads, overvejer et sommerhus i træ eller ønsker en ny garage eller carport opført på grunden, tænker vi løsningen ind i en sammenhængende helhed.
            </p>
            <Link 
              to="/kontakt" 
              className="bg-accent hover:bg-accent-light text-white px-6 py-3 rounded-md text-sm font-semibold tracking-wide transition-all duration-300"
            >
              Få en uforpligtende snak
            </Link>
          </div>
        </div>
      </section>

      {/* Hvad koster en tømrer? */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
            PRISER & TILBUD
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
            Hvad koster en tømrer?
          </h2>
          <p className="text-stone-600 text-base leading-relaxed max-w-3xl">
            Prisen på tømrerarbejde afhænger af opgavens art, materialevalg, konstruktion og adgangsforhold. Hvad end der er tale om udskiftning af vinduer, et nyt træudhus eller en ny tilbygning, sikrer vi altid et gennemskueligt og gennemtænkt tilbud.
          </p>

          <div className="w-full text-left bg-stone-50 border border-stone-200/60 rounded-2xl p-8 mt-4">
            <h4 className="font-display font-bold text-stone-900 text-lg mb-4 text-center">
              Når vi vurderer priser, ser vi typisk på:
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-sm shrink-0 shadow-sm">
                  <i className="fa-solid fa-calculator" />
                </div>
                <div>
                  <h5 className="font-bold text-stone-900 text-sm">Opgavens omfang</h5>
                  <p className="text-stone-500 text-xs mt-0.5">Størrelse, omfang og adgangsforhold.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-sm shrink-0 shadow-sm">
                  <i className="fa-solid fa-tree" />
                </div>
                <div>
                  <h5 className="font-bold text-stone-900 text-sm">Materialer & detaljer</h5>
                  <p className="text-stone-500 text-xs mt-0.5">Træsort, detaljer og tilpasninger.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-sm shrink-0 shadow-sm">
                  <i className="fa-solid fa-calendar-days" />
                </div>
                <div>
                  <h5 className="font-bold text-stone-900 text-sm">Tidsplan</h5>
                  <p className="text-stone-500 text-xs mt-0.5">Aftalt tidsramme for færdiggørelse.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-sm shrink-0 shadow-sm">
                  <i className="fa-solid fa-users" />
                </div>
                <div>
                  <h5 className="font-bold text-stone-900 text-sm">Koordinering</h5>
                  <p className="text-stone-500 text-xs mt-0.5">Styring af hele tømrerentreprisen.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-stone-600 text-sm leading-relaxed mt-2">
            Du kan altid få et skriftligt tilbud, hvor vi gennemgår opgaven i ro og mag, så du kender alle detaljer.
          </p>
        </div>
      </section>

      {/* Virksomhedens Styrker */}
      <section className="py-24 bg-stone-50 border-t border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              Vores værdier
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
              Vores styrker
            </h2>
            <p className="text-stone-600 text-sm sm:text-base">
              De værdier og principper, der sikrer dig et perfekt resultat og en tryg byggeproces fra start til slut.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strengths.map((strength) => (
              <div 
                key={strength.id}
                className="bg-white p-8 rounded-xl border border-stone-200/60 flex flex-col gap-4 shadow-sm"
              >
                <div className="w-12 h-12 rounded-lg bg-stone-50 border border-stone-200 flex items-center justify-center text-accent text-xl shadow-sm">
                  <i className={strength.icon} />
                </div>
                <h3 className="font-display text-xl font-bold text-stone-900">
                  {strength.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {strength.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referencer / Udvalgte Projekter */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-16">
            <div className="flex flex-col gap-4 max-w-2xl">
              <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
                Vores arbejde
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
                Galleri & billeder
              </h2>
              <p className="text-stone-600 text-sm sm:text-base">
                Se udvalgte billeder af vores arbejde med træbyggeri, udhuse, terrasser og renoveringer.
              </p>
            </div>
            <Link 
              to="/referencer" 
              className="bg-stone-900 hover:bg-stone-800 text-white px-6 py-3.5 rounded-md text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap flex items-center gap-2"
            >
              <span>Se hele galleriet</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-accent" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {references.map((project) => {
                const coverImg = project.forsidebillede?.[0] || project.galleri?.[0] || '';
                const resizedCover = coverImg 
                  ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,height=450,fit=cover,quality=80,format=auto/${coverImg.replace('https://assets.auxodev.dk/', '')}`
                  : '';

                return (
                  <Link 
                    key={project.id} 
                    to="/referencer"
                    className="group flex flex-col gap-4"
                  >
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border border-stone-200 shadow-sm relative">
                      <img 
                        src={resizedCover} 
                        alt={project.titel?.da || project.titel} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-stone-950/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white/95 text-stone-900 px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase shadow-md">
                          Se projekt
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                        {project.ydelse?.da || project.ydelse} · {project.lokation?.da || project.lokation}
                      </span>
                      <h3 className="font-display text-xl font-bold text-stone-900 group-hover:text-accent transition-colors">
                        {project.titel?.da || project.titel}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Kundeudtalelser */}
      {/* Kundeudtalelser er deaktiveret */}

      {/* Kontaktsektion */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Info Column */}
          <div className="lg:col-span-5 flex flex-col items-start gap-6">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              UFORPLIGTENDE TILBUD
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Kontakt os og få et uforpligtende tilbud
            </h2>
            <p className="text-stone-600 leading-relaxed">
              Vil du have en tømrer, der leverer kvalitet, holder aftaler og sikrer personlig kontakt gennem hele forløbet?
            </p>
            <p className="text-stone-600 leading-relaxed">
              Hos Munkgaard Træbyg ApS kan du ringe på **+45 30 94 38 90** eller skrive til **jacob@munkgaardtraebyg.dk**, så tager vi en uforpligtende snak om dit byggeprojekt. Vi kører gerne ud i Bording, Ikast, Engesvang, Kragelund, Silkeborg og omegn for at besigtige opgaven.
            </p>

            <div className="flex flex-col gap-4 w-full text-sm mt-2">
              <a 
                href={contactInfo.mapsUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-lg shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-stone-900">Adresse</span>
                  <span className="text-stone-600 mt-0.5">{contactInfo.address}</span>
                </div>
              </a>

              <a 
                href={`tel:${contactInfo.phoneRaw}`} 
                className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group whitespace-nowrap"
              >
                <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-lg shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  <i className="fa-solid fa-phone" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-stone-900">Telefon</span>
                  <span className="text-stone-600 mt-0.5">{contactInfo.phone}</span>
                </div>
              </a>

              <a 
                href={`mailto:${contactInfo.email}`} 
                className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group whitespace-nowrap"
              >
                <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-lg shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-stone-900">E-mail</span>
                  <span className="text-stone-600 mt-0.5">{contactInfo.email}</span>
                </div>
              </a>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}

// Fallback data
const fallbackReferences = [
  {
    id: "ref-1",
    titel: { da: "Gennemtænkt træbyggeri" },
    ydelse: { da: "Nybyggeri i træ" },
    lokation: { da: "Bording" },
    forsidebillede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg"]
  },
  {
    id: "ref-2",
    titel: { da: "Moderne garage og carport" },
    ydelse: { da: "Udhuse" },
    lokation: { da: "Ikast" },
    forsidebillede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111542.jpg"]
  },
  {
    id: "ref-3",
    titel: { da: "Arkitektonisk tilbygning" },
    ydelse: { da: "Tilbygning" },
    lokation: { da: "Silkeborg" },
    forsidebillede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg"]
  }
];

const fallbackTestimonials = [
  {
    id: "test-1",
    navn: { da: "Morten Hansen" },
    rating: 5,
    udtalelse: { da: "Aftalerne blev overholdt til punkt og prikke. Fantastisk finish og én fast kontaktperson gennem hele forløbet." }
  },
  {
    id: "test-2",
    navn: { da: "Kirsten og Peter" },
    rating: 5,
    udtalelse: { da: "Vi fik lavet en arkitektonisk flot tilbygning. Jacob formåede at tilpasse den perfekt til vores hus. Meget tilfredse!" }
  },
  {
    id: "test-3",
    navn: { da: "Sofie Nielsen, Bording" },
    rating: 5,
    udtalelse: { da: "God kommunikation og virkelig flot tømrerhåndværk. Kan varmt anbefale Munkgaard Træbyg." }
  }
];

function ServiceTeaserCard({ title, href, img, desc }) {
  return (
    <Link to={href} className="group bg-white rounded-xl overflow-hidden border border-stone-200 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
      <div className="aspect-[16/10] overflow-hidden">
        <img 
          src={img} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-6 flex flex-col gap-2 flex-grow">
        <h3 className="font-display text-lg font-bold text-stone-900 group-hover:text-accent transition-colors">{title}</h3>
        <p className="text-stone-500 text-xs leading-relaxed line-clamp-2">{desc}</p>
        <span className="text-accent font-semibold text-xs mt-auto pt-4 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
          Læs mere <i className="fa-solid fa-arrow-right text-[10px]" />
        </span>
      </div>
    </Link>
  );
}
